import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { fetchFunnels } from '@/card/dealApi'
import { RecordPicker } from '@/card/RecordPicker'
import { bulkReassign, bulkStage, type BulkDomainResult, type BulkSelection } from './importApi'

/**
 * Массовые доменные действия списков продаж (тикет #107): новый ответственный, новый этап.
 *
 * Работает и по выбранным строкам, и по фильтру целиком: «все сделки уволившегося» — это
 * фильтр, а отметить двести строк на ленте, которая грузится по сотне, значит отметить первую
 * сотню. Результат называет изменённое и пропущенное с причиной: закрытая сделка не
 * переназначается (её ответственный — часть истории KPI), и это видно, а не молча.
 */

export const REASSIGNABLE = new Set(['sales.deal', 'sales.lead', 'sales.counterparty'])

export function SalesBulkActions({
  entity,
  selection,
  onDone,
}: {
  entity: string
  selection: BulkSelection
  onDone: (message: string) => void
}) {
  const { t } = useApp()
  const queryClient = useQueryClient()
  const [employee, setEmployee] = useState<{ id: string; label: string } | null>(null)
  const [stage, setStage] = useState('')
  const funnels = useQuery({
    queryKey: ['deal-funnels'],
    queryFn: ({ signal }) => fetchFunnels(signal),
    enabled: entity === 'sales.deal',
  })
  const report = (result: BulkDomainResult) => {
    void queryClient.invalidateQueries({ queryKey: ['list', entity] })
    const skipped = result.skipped.length > 0
      ? ' ' + t('bulk.skipped', { reasons: [...new Set(result.skipped.map((s) => s.reason))].join('; ') })
      : ''
    onDone(t('bulk.domainResult', { affected: result.affected, requested: result.requested }) + skipped)
  }
  const reassign = useMutation({
    mutationFn: () => bulkReassign(entity, selection, employee?.id ?? ''),
    onSuccess: (result) => {
      setEmployee(null)
      report(result)
    },
    onError: (error) => onDone((error as Error).message),
  })
  const move = useMutation({
    mutationFn: () => bulkStage(selection, stage),
    onSuccess: (result) => {
      setStage('')
      report(result)
    },
    onError: (error) => onDone((error as Error).message),
  })
  // Lost needs a reason per deal; a bulk move offers the stages it can make honestly.
  const stages = (funnels.data ?? []).flatMap((funnel) =>
    funnel.stages.filter((s) => s.stageType !== 'lost').map((s) => ({ code: s.code, label: `${funnel.name}: ${s.name}` })))
  const uniqueStages = stages.filter((s, i) => stages.findIndex((o) => o.code === s.code) === i)

  return (
    <span className="report-actions">
      <label htmlFor={`bulk-employee-${entity}`}>{t('bulk.newResponsible')}</label>
      <RecordPicker id={`bulk-employee-${entity}`} entity="hr.employee" searchColumn="last_name" extraColumn="first_name"
                    value={employee} onChange={setEmployee} />
      <button type="button" disabled={!employee || reassign.isPending} onClick={() => reassign.mutate()}>
        {t('bulk.reassign')}
      </button>
      {entity === 'sales.deal' && (
        <>
          <select aria-label={t('bulk.newStage')} value={stage} onChange={(e) => setStage(e.target.value)}>
            <option value="">{t('bulk.newStage')}</option>
            {uniqueStages.map((s) => (
              <option key={s.code} value={s.code}>{s.label}</option>
            ))}
          </select>
          <button type="button" disabled={!stage || move.isPending} onClick={() => move.mutate()}>
            {t('bulk.move')}
          </button>
        </>
      )}
    </span>
  )
}
